import { useCallback } from 'react';

import { emitSyncConceptGroupSelections } from '@verantos-dev/app-state-research-assistant';
import {
  isConceptRefKey,
  isDomainFilter,
  isFilterGroup,
  isTemporalGroup,
  type LogicNode,
} from '@verantos-dev/feature-analytical-variables';
import type { AnalyticalVariable } from '@verantos-dev/types-cohorts';

interface ConceptRefDescriptor {
  /** AV concept_ref.id — used as the cross-turn anchor by the workflow's extract_filters. */
  anchorId: string;
  /** OMOP table on the parent DomainFilter (e.g. "MEASUREMENT"). */
  containerType: string;
  /** AV field name in snake_case as the backend expects ("concept_ref", "value_as_concept_ref"). */
  attribute: string;
  /** Display name on the concept_ref (preserved verbatim as the saved selection's span). */
  name: string;
  /** Selected concept group IDs in their stored order. */
  refIds: string[];
}

const camelToSnake = (s: string): string => s.replace(/([A-Z])/g, '_$1').toLowerCase();

/** Walk an AV's logic tree and return one descriptor per resolved ConceptGroupRef. */
const collectConceptRefDescriptors = (
  logic: LogicNode | undefined | null
): ConceptRefDescriptor[] => {
  const out: ConceptRefDescriptor[] = [];

  const visit = (node: LogicNode | undefined | null): void => {
    if (!node) return;

    if (isDomainFilter(node)) {
      const record = node as unknown as Record<string, unknown>;
      const tableValue = record.table;
      const containerType = typeof tableValue === 'string' ? tableValue.toUpperCase() : '';

      for (const key of Object.keys(record)) {
        if (!isConceptRefKey(key)) continue;
        const value = record[key] as Record<string, unknown> | undefined;
        if (!value) continue;
        // Only emit for fully-resolved concept_group refs; concept_group_list is unresolved.
        if (value.type !== 'concept_group') continue;
        const anchorId = typeof value.id === 'string' ? value.id : '';
        const name = typeof value.name === 'string' ? value.name : '';
        const refIds = Array.isArray(value.refIds) ? (value.refIds as string[]) : [];
        if (!anchorId || !containerType) continue;
        out.push({
          anchorId,
          containerType,
          attribute: camelToSnake(key),
          name,
          refIds,
        });
      }

      const correlated = record.correlatedFilters as Array<{ filter: unknown }> | undefined;
      if (correlated) {
        for (const entry of correlated) {
          visit(entry.filter as LogicNode);
        }
      }
      return;
    }

    if (isFilterGroup(node)) {
      node.filters.forEach((child) => visit(child as LogicNode));
      return;
    }

    if (isTemporalGroup(node)) {
      node.filters.forEach((child) => visit(child as LogicNode));
    }
  };

  visit(logic);
  return out;
};

const arraysShallowEqual = (a: readonly string[], b: readonly string[]): boolean => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

interface AVConceptGroupSelectionSyncInputs {
  cohortId: string | undefined;
  conceptGroupIdToNameMap: Map<string, string>;
}

/**
 * Returns a callback that diffs an AV's concept_ref selections before/after a UI edit
 * and fires `sync_concept_group_selections` for each concept_ref whose refIds changed.
 *
 * The backend persists the upserted selection so that on the next chat turn,
 * `concept_group_selection_workflow.apply_and_collect` re-applies the user's refIds —
 * even if the AV regen LLM dropped or rewrote the concept_ref (OR-5840).
 */
export const useAVConceptGroupSelectionSync = ({
  cohortId,
  conceptGroupIdToNameMap,
}: AVConceptGroupSelectionSyncInputs) => {
  return useCallback(
    (priorAv: AnalyticalVariable | undefined, nextAv: AnalyticalVariable | undefined): void => {
      if (!cohortId || !nextAv) return;

      const containerName = nextAv.name?.trim();
      if (!containerName) return;

      const priorByAnchor = new Map<string, ConceptRefDescriptor>();
      for (const desc of collectConceptRefDescriptors(priorAv?.logic ?? null)) {
        priorByAnchor.set(desc.anchorId, desc);
      }

      const changed: ConceptRefDescriptor[] = [];
      for (const desc of collectConceptRefDescriptors(nextAv.logic)) {
        const prior = priorByAnchor.get(desc.anchorId);
        if (!prior || !arraysShallowEqual(prior.refIds, desc.refIds)) {
          changed.push(desc);
        }
      }

      if (changed.length === 0) return;

      emitSyncConceptGroupSelections({
        cohortId,
        selections: changed.map((desc) => ({
          containerName,
          containerType: desc.containerType,
          table: desc.containerType,
          filterType: 'avFilter',
          attribute: desc.attribute,
          span: desc.name,
          anchorId: desc.anchorId,
          selectedConceptGroups: desc.refIds.map((id) => ({
            id,
            name: conceptGroupIdToNameMap.get(id) ?? id,
          })),
          status: 'success',
          reported: true,
        })),
      });
    },
    [cohortId, conceptGroupIdToNameMap]
  );
};
